/**
 * Entity Framework Domain Seed Data
 *
 * Contains all seed data for Entity Framework Service (32):
 * - Entity Definitions (custom entities like Loan, Property, Contact)
 * - Field Definitions (fields within entities)
 * - Entity Templates (pre-built templates)
 * - Entity Records (sample data)
 * - Relationship Definitions
 * - Matching Rules (identity resolution)
 *
 * @see 32-entity-framework-service/prisma/schema.prisma
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// ENTITY DEFINITION IDs
// =============================================================================

export const ENTITY_DEFINITION_IDS = {
  // Loan domain entities
  LOAN_APPLICATION: seedId('entity-loan-app', 1),
  PROPERTY: seedId('entity-property', 2),
  COLLATERAL: seedId('entity-collateral', 3),

  // CRM domain entities
  CONTACT: seedId('entity-contact', 4),
  COMPANY: seedId('entity-company', 5),
  DEAL: seedId('entity-deal', 6),
  PROPOSAL: seedId('entity-proposal', 9), // Issue #470: Required for CRM proposal generation

  // Loan term sheet entities
  TERM_SHEET: seedId('entity-term-sheet', 10),
  TERM_SHEET_TEMPLATE: seedId('entity-term-sheet-tpl', 11),

  // Support domain entities
  TICKET: seedId('entity-ticket', 7),
  KNOWLEDGE_ARTICLE: seedId('entity-kb-article', 8),
} as const;

export type EntityDefinitionId = (typeof ENTITY_DEFINITION_IDS)[keyof typeof ENTITY_DEFINITION_IDS];

// =============================================================================
// FIELD DEFINITION IDs
// =============================================================================

export const FIELD_DEFINITION_IDS = {
  // Loan Application fields
  LOAN_APP_NUMBER: seedId('field-loan-number', 1),
  LOAN_APP_AMOUNT: seedId('field-loan-amount', 2),
  LOAN_APP_TYPE: seedId('field-loan-type', 3),
  LOAN_APP_STATUS: seedId('field-loan-status', 4),
  LOAN_APP_LTV: seedId('field-loan-ltv', 5),
  LOAN_APP_PURPOSE: seedId('field-loan-purpose', 6),
  LOAN_APP_TERM: seedId('field-loan-term', 7),
  LOAN_APP_RATE: seedId('field-loan-rate', 8),

  // Property fields
  PROPERTY_ADDRESS: seedId('field-prop-address', 1),
  PROPERTY_TYPE: seedId('field-prop-type', 2),
  PROPERTY_VALUE: seedId('field-prop-value', 3),
  PROPERTY_BEDS: seedId('field-prop-beds', 4),
  PROPERTY_BATHS: seedId('field-prop-baths', 5),
  PROPERTY_SQFT: seedId('field-prop-sqft', 6),

  // Contact fields
  CONTACT_FIRST_NAME: seedId('field-contact-fname', 1),
  CONTACT_LAST_NAME: seedId('field-contact-lname', 2),
  CONTACT_EMAIL: seedId('field-contact-email', 3),
  CONTACT_PHONE: seedId('field-contact-phone', 4),
  CONTACT_COMPANY: seedId('field-contact-company', 5),
} as const;

export type FieldDefinitionId = (typeof FIELD_DEFINITION_IDS)[keyof typeof FIELD_DEFINITION_IDS];

// =============================================================================
// ENTITY TEMPLATE IDs
// =============================================================================

export const ENTITY_TEMPLATE_IDS = {
  CRM_TEMPLATE: seedId('template-crm', 1),
  LOS_TEMPLATE: seedId('template-los', 2),
  SUPPORT_TEMPLATE: seedId('template-support', 3),
  REAL_ESTATE_TEMPLATE: seedId('template-realestate', 4),
} as const;

export type EntityTemplateId = (typeof ENTITY_TEMPLATE_IDS)[keyof typeof ENTITY_TEMPLATE_IDS];

// =============================================================================
// ENTITY RECORD IDs
// =============================================================================

export const ENTITY_RECORD_IDS = {
  // Sample loan applications
  LOAN_MARCUS_THOMPSON: seedId('record-loan-marcus', 1),
  LOAN_JENNIFER_WALSH: seedId('record-loan-jennifer', 2),
  LOAN_ROBERT_CHEN: seedId('record-loan-robert', 3),

  // Sample properties
  PROPERTY_AUSTIN_OFFICE: seedId('record-prop-austin', 1),
  PROPERTY_DALLAS_RETAIL: seedId('record-prop-dallas', 2),
  PROPERTY_HOUSTON_WAREHOUSE: seedId('record-prop-houston', 3),

  // Sample contacts
  CONTACT_MARCUS_THOMPSON: seedId('record-contact-marcus', 1),
  CONTACT_JENNIFER_WALSH: seedId('record-contact-jennifer', 2),
} as const;

export type EntityRecordId = (typeof ENTITY_RECORD_IDS)[keyof typeof ENTITY_RECORD_IDS];

// =============================================================================
// RELATIONSHIP DEFINITION IDs
// =============================================================================

export const RELATIONSHIP_DEFINITION_IDS = {
  LOAN_TO_PROPERTY: seedId('rel-loan-property', 1),
  LOAN_TO_BORROWER: seedId('rel-loan-borrower', 2),
  CONTACT_TO_COMPANY: seedId('rel-contact-company', 3),
  DEAL_TO_CONTACT: seedId('rel-deal-contact', 4),
} as const;

export type RelationshipDefinitionId = (typeof RELATIONSHIP_DEFINITION_IDS)[keyof typeof RELATIONSHIP_DEFINITION_IDS];

// =============================================================================
// MATCHING RULE IDs
// =============================================================================

export const MATCHING_RULE_IDS = {
  CONTACT_EMAIL_MATCH: seedId('match-contact-email', 1),
  CONTACT_PHONE_MATCH: seedId('match-contact-phone', 2),
  BORROWER_SSN_MATCH: seedId('match-borrower-ssn', 3),
  PROPERTY_ADDRESS_MATCH: seedId('match-prop-address', 4),
} as const;

export type MatchingRuleId = (typeof MATCHING_RULE_IDS)[keyof typeof MATCHING_RULE_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export type FieldType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'RICH_TEXT'
  | 'NUMBER'
  | 'CURRENCY'
  | 'PERCENTAGE'
  | 'DATE'
  | 'DATETIME'
  | 'BOOLEAN'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'LOOKUP'
  | 'EMAIL'
  | 'PHONE'
  | 'URL'
  | 'ADDRESS'
  | 'AUTO_NUMBER';

export interface EntityDefinitionSeedData {
  id: string;
  businessId: string;
  name: string;
  pluralName: string;
  label: string;
  pluralLabel: string;
  description?: string;
  icon?: string;
  color?: string;
  allowCreate: boolean;
  allowEdit: boolean;
  allowDelete: boolean;
  primaryField: string;
  secondaryField?: string;
  pipelineEnabled: boolean;
  isSystem: boolean;
  isActive: boolean;
  createdBy: string;
}

export interface FieldDefinitionSeedData {
  id: string;
  entityId: string;
  name: string;
  label: string;
  description?: string;
  fieldType: FieldType;
  required: boolean;
  unique: boolean;
  minLength?: number;
  maxLength?: number;
  options?: Array<{ value: string; label: string; color?: string }>;
  placeholder?: string;
  helpText?: string;
  section?: string;
  displayOrder: number;
  showInList: boolean;
  showInCreate: boolean;
  showInDetail: boolean;
  showInSearch: boolean;
  isSystem: boolean;
}

export interface EntityTemplateSeedData {
  id: string;
  name: string;
  label: string;
  description?: string;
  category: string;
  icon?: string;
  definition: {
    entities: Array<{
      name: string;
      label: string;
      fields: Array<{ name: string; label: string; type: FieldType }>;
    }>;
  };
  version: string;
  isDefault: boolean;
}

export interface EntityRecordSeedData {
  id: string;
  businessId: string;
  entityId: string;
  data: Record<string, unknown>;
  searchText?: string;
  pipelineId?: string;
  stageId?: string;
  ownerId?: string;
  createdBy: string;
}

export interface RelationshipDefinitionSeedData {
  id: string;
  businessId: string;
  name: string;
  fromEntityId: string;
  fromFieldName: string;
  fromLabel: string;
  toEntityId: string;
  toFieldName: string;
  toLabel: string;
  relationshipType: 'ONE_TO_ONE' | 'ONE_TO_MANY' | 'MANY_TO_MANY';
  cascadeDelete: boolean;
  required: boolean;
}

export interface MatchingRuleSeedData {
  id: string;
  businessId: string;
  entityId: string;
  name: string;
  description?: string;
  priority: number;
  isActive: boolean;
  criteria: Array<{
    fieldName: string;
    criteriaType: 'EXACT' | 'FUZZY_TEXT' | 'PHONETIC' | 'NORMALIZED' | 'HASH';
    weight: number;
    options?: Record<string, unknown>;
  }>;
  exactThreshold: number;
  probableThreshold: number;
  possibleThreshold: number;
  defaultAction: 'AUTO_LINK' | 'FLAG_REVIEW' | 'SUGGEST' | 'BLOCK' | 'IGNORE';
  createdBy: string;
}

// =============================================================================
// ENTITY DEFINITION DATA
// =============================================================================

export const ENTITY_DEFINITIONS: Record<keyof typeof ENTITY_DEFINITION_IDS, EntityDefinitionSeedData> = {
  LOAN_APPLICATION: {
    id: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'loan_application',
    pluralName: 'loan_applications',
    label: 'Loan Application',
    pluralLabel: 'Loan Applications',
    description: 'Commercial loan applications with all borrower and property details',
    icon: 'file-text',
    color: '#3B82F6',
    allowCreate: true,
    allowEdit: true,
    allowDelete: false,
    primaryField: 'loan_number',
    secondaryField: 'borrower_name',
    pipelineEnabled: true,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PROPERTY: {
    id: ENTITY_DEFINITION_IDS.PROPERTY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'property',
    pluralName: 'properties',
    label: 'Property',
    pluralLabel: 'Properties',
    description: 'Real estate properties used as collateral or investment targets',
    icon: 'building',
    color: '#10B981',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'address',
    secondaryField: 'property_type',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  COLLATERAL: {
    id: ENTITY_DEFINITION_IDS.COLLATERAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'collateral',
    pluralName: 'collaterals',
    label: 'Collateral',
    pluralLabel: 'Collaterals',
    description: 'Assets pledged as security for loans',
    icon: 'shield',
    color: '#F59E0B',
    allowCreate: true,
    allowEdit: true,
    allowDelete: false,
    primaryField: 'description',
    secondaryField: 'value',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CONTACT: {
    id: ENTITY_DEFINITION_IDS.CONTACT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'contact',
    pluralName: 'contacts',
    label: 'Contact',
    pluralLabel: 'Contacts',
    description: 'People and business contacts including borrowers and partners',
    icon: 'user',
    color: '#8B5CF6',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'full_name',
    secondaryField: 'email',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  COMPANY: {
    id: ENTITY_DEFINITION_IDS.COMPANY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'company',
    pluralName: 'companies',
    label: 'Company',
    pluralLabel: 'Companies',
    description: 'Business entities and organizations',
    icon: 'briefcase',
    color: '#EF4444',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'name',
    secondaryField: 'industry',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  DEAL: {
    id: ENTITY_DEFINITION_IDS.DEAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'deal',
    pluralName: 'deals',
    label: 'Deal',
    pluralLabel: 'Deals',
    description: 'Sales opportunities and business deals',
    icon: 'trending-up',
    color: '#06B6D4',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'name',
    secondaryField: 'value',
    pipelineEnabled: true,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  // Issue #470: Required for CRM proposal generation
  PROPOSAL: {
    id: ENTITY_DEFINITION_IDS.PROPOSAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'proposal',
    pluralName: 'proposals',
    label: 'Proposal',
    pluralLabel: 'Proposals',
    description: 'Sales proposals and quotes generated from deals',
    icon: 'file-check',
    color: '#A855F7',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'title',
    secondaryField: 'status',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  TERM_SHEET: {
    id: ENTITY_DEFINITION_IDS.TERM_SHEET,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'term_sheet',
    pluralName: 'term_sheets',
    label: 'Term Sheet',
    pluralLabel: 'Term Sheets',
    description: 'Loan term sheet proposals sent to borrowers with pricing and conditions',
    icon: 'file-spreadsheet',
    color: '#0EA5E9',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'term_sheet_number',
    secondaryField: 'status',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  TERM_SHEET_TEMPLATE: {
    id: ENTITY_DEFINITION_IDS.TERM_SHEET_TEMPLATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'term_sheet_template',
    pluralName: 'term_sheet_templates',
    label: 'Term Sheet Template',
    pluralLabel: 'Term Sheet Templates',
    description: 'Reusable templates for term sheet generation with branding and default terms',
    icon: 'layout-template',
    color: '#6366F1',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'name',
    secondaryField: 'loan_type',
    pipelineEnabled: false,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  TICKET: {
    id: ENTITY_DEFINITION_IDS.TICKET,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'ticket',
    pluralName: 'tickets',
    label: 'Support Ticket',
    pluralLabel: 'Support Tickets',
    description: 'Customer support tickets and requests',
    icon: 'message-square',
    color: '#EC4899',
    allowCreate: true,
    allowEdit: true,
    allowDelete: false,
    primaryField: 'subject',
    secondaryField: 'status',
    pipelineEnabled: true,
    isSystem: true,
    isActive: true,
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  KNOWLEDGE_ARTICLE: {
    id: ENTITY_DEFINITION_IDS.KNOWLEDGE_ARTICLE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'knowledge_article',
    pluralName: 'knowledge_articles',
    label: 'Knowledge Article',
    pluralLabel: 'Knowledge Articles',
    description: 'Help articles and documentation',
    icon: 'book-open',
    color: '#14B8A6',
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    primaryField: 'title',
    secondaryField: 'category',
    pipelineEnabled: false,
    isSystem: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

// =============================================================================
// FIELD DEFINITION DATA (Sample fields for Loan Application entity)
// =============================================================================

export const FIELD_DEFINITIONS: Record<string, FieldDefinitionSeedData> = {
  // Loan Application fields
  LOAN_APP_NUMBER: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_NUMBER,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'loan_number',
    label: 'Loan Number',
    description: 'Unique identifier for the loan application',
    fieldType: 'AUTO_NUMBER',
    required: true,
    unique: true,
    displayOrder: 1,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  LOAN_APP_AMOUNT: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_AMOUNT,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'loan_amount',
    label: 'Loan Amount',
    description: 'Requested loan amount',
    fieldType: 'CURRENCY',
    required: true,
    unique: false,
    placeholder: 'Enter loan amount',
    section: 'Loan Details',
    displayOrder: 2,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
    isSystem: true,
  },
  LOAN_APP_TYPE: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_TYPE,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'loan_type',
    label: 'Loan Type',
    description: 'Type of loan product',
    fieldType: 'SELECT',
    required: true,
    unique: false,
    options: [
      { value: 'bridge', label: 'Bridge Loan', color: '#3B82F6' },
      { value: 'fix_flip', label: 'Fix & Flip', color: '#10B981' },
      { value: 'dscr', label: 'DSCR', color: '#F59E0B' },
      { value: 'construction', label: 'Construction', color: '#EF4444' },
      { value: 'ground_up', label: 'Ground Up', color: '#8B5CF6' },
    ],
    section: 'Loan Details',
    displayOrder: 3,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  LOAN_APP_STATUS: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_STATUS,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'status',
    label: 'Status',
    description: 'Current application status',
    fieldType: 'SELECT',
    required: true,
    unique: false,
    options: [
      { value: 'draft', label: 'Draft', color: '#9CA3AF' },
      { value: 'submitted', label: 'Submitted', color: '#3B82F6' },
      { value: 'in_review', label: 'In Review', color: '#F59E0B' },
      { value: 'approved', label: 'Approved', color: '#10B981' },
      { value: 'funded', label: 'Funded', color: '#8B5CF6' },
      { value: 'rejected', label: 'Rejected', color: '#EF4444' },
    ],
    section: 'Status',
    displayOrder: 4,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  LOAN_APP_LTV: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_LTV,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'ltv_ratio',
    label: 'LTV Ratio',
    description: 'Loan-to-Value ratio',
    fieldType: 'PERCENTAGE',
    required: false,
    unique: false,
    section: 'Underwriting',
    displayOrder: 5,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
    isSystem: false,
  },
  LOAN_APP_PURPOSE: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_PURPOSE,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'purpose',
    label: 'Loan Purpose',
    description: 'Purpose of the loan',
    fieldType: 'SELECT',
    required: true,
    unique: false,
    options: [
      { value: 'purchase', label: 'Purchase' },
      { value: 'refinance', label: 'Refinance' },
      { value: 'cash_out', label: 'Cash Out' },
      { value: 'construction', label: 'Construction' },
    ],
    section: 'Loan Details',
    displayOrder: 6,
    showInList: false,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
    isSystem: false,
  },
  LOAN_APP_TERM: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_TERM,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'term_months',
    label: 'Term (Months)',
    description: 'Loan term in months',
    fieldType: 'NUMBER',
    required: true,
    unique: false,
    section: 'Loan Details',
    displayOrder: 7,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
    isSystem: false,
  },
  LOAN_APP_RATE: {
    id: FIELD_DEFINITION_IDS.LOAN_APP_RATE,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    name: 'interest_rate',
    label: 'Interest Rate',
    description: 'Annual interest rate',
    fieldType: 'PERCENTAGE',
    required: false,
    unique: false,
    section: 'Terms',
    displayOrder: 8,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: false,
    isSystem: false,
  },

  // Property fields
  PROPERTY_ADDRESS: {
    id: FIELD_DEFINITION_IDS.PROPERTY_ADDRESS,
    entityId: ENTITY_DEFINITION_IDS.PROPERTY,
    name: 'address',
    label: 'Address',
    fieldType: 'ADDRESS',
    required: true,
    unique: false,
    displayOrder: 1,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  PROPERTY_TYPE: {
    id: FIELD_DEFINITION_IDS.PROPERTY_TYPE,
    entityId: ENTITY_DEFINITION_IDS.PROPERTY,
    name: 'property_type',
    label: 'Property Type',
    fieldType: 'SELECT',
    required: true,
    unique: false,
    options: [
      { value: 'sfr', label: 'Single Family Residential' },
      { value: 'multi_family', label: 'Multi-Family' },
      { value: 'commercial', label: 'Commercial' },
      { value: 'industrial', label: 'Industrial' },
      { value: 'mixed_use', label: 'Mixed Use' },
      { value: 'land', label: 'Land' },
    ],
    displayOrder: 2,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  PROPERTY_VALUE: {
    id: FIELD_DEFINITION_IDS.PROPERTY_VALUE,
    entityId: ENTITY_DEFINITION_IDS.PROPERTY,
    name: 'value',
    label: 'Property Value',
    fieldType: 'CURRENCY',
    required: false,
    unique: false,
    displayOrder: 3,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
    isSystem: false,
  },

  // Contact fields
  CONTACT_FIRST_NAME: {
    id: FIELD_DEFINITION_IDS.CONTACT_FIRST_NAME,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'first_name',
    label: 'First Name',
    fieldType: 'TEXT',
    required: true,
    unique: false,
    maxLength: 100,
    displayOrder: 1,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  CONTACT_LAST_NAME: {
    id: FIELD_DEFINITION_IDS.CONTACT_LAST_NAME,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'last_name',
    label: 'Last Name',
    fieldType: 'TEXT',
    required: true,
    unique: false,
    maxLength: 100,
    displayOrder: 2,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  CONTACT_EMAIL: {
    id: FIELD_DEFINITION_IDS.CONTACT_EMAIL,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'email',
    label: 'Email',
    fieldType: 'EMAIL',
    required: false,
    unique: true,
    displayOrder: 3,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
    isSystem: true,
  },
  CONTACT_PHONE: {
    id: FIELD_DEFINITION_IDS.CONTACT_PHONE,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'phone',
    label: 'Phone',
    fieldType: 'PHONE',
    required: false,
    unique: false,
    displayOrder: 4,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
    isSystem: true,
  },
};

// =============================================================================
// ENTITY TEMPLATE DATA
// =============================================================================

export const ENTITY_TEMPLATES: Record<keyof typeof ENTITY_TEMPLATE_IDS, EntityTemplateSeedData> = {
  CRM_TEMPLATE: {
    id: ENTITY_TEMPLATE_IDS.CRM_TEMPLATE,
    name: 'crm',
    label: 'CRM Template',
    description: 'Customer Relationship Management entities: Contacts, Companies, Deals, Proposals',
    category: 'sales',
    icon: 'users',
    definition: {
      entities: [
        {
          name: 'contact',
          label: 'Contact',
          fields: [
            { name: 'first_name', label: 'First Name', type: 'TEXT' },
            { name: 'last_name', label: 'Last Name', type: 'TEXT' },
            { name: 'email', label: 'Email', type: 'EMAIL' },
            { name: 'phone', label: 'Phone', type: 'PHONE' },
          ],
        },
        {
          name: 'company',
          label: 'Company',
          fields: [
            { name: 'name', label: 'Company Name', type: 'TEXT' },
            { name: 'industry', label: 'Industry', type: 'SELECT' },
            { name: 'website', label: 'Website', type: 'URL' },
          ],
        },
        {
          name: 'deal',
          label: 'Deal',
          fields: [
            { name: 'name', label: 'Deal Name', type: 'TEXT' },
            { name: 'value', label: 'Deal Value', type: 'CURRENCY' },
            { name: 'stage', label: 'Stage', type: 'SELECT' },
          ],
        },
        // Issue #470: Required for CRM proposal generation
        {
          name: 'proposal',
          label: 'Proposal',
          fields: [
            { name: 'title', label: 'Title', type: 'TEXT' },
            { name: 'deal_id', label: 'Deal ID', type: 'TEXT' },
            { name: 'status', label: 'Status', type: 'SELECT' },
            { name: 'valid_until', label: 'Valid Until', type: 'DATE' },
            { name: 'total_amount', label: 'Total Amount', type: 'CURRENCY' },
          ],
        },
        // Required for term sheet generation (CRM service → entity framework)
        {
          name: 'term_sheet',
          label: 'Term Sheet',
          fields: [
            { name: 'term_sheet_number', label: 'Term Sheet Number', type: 'AUTO_NUMBER' },
            { name: 'borrower_name', label: 'Borrower Name', type: 'TEXT' },
            { name: 'property_address', label: 'Property Address', type: 'ADDRESS' },
            { name: 'loan_amount', label: 'Loan Amount', type: 'CURRENCY' },
            { name: 'interest_rate', label: 'Interest Rate', type: 'PERCENTAGE' },
            { name: 'loan_term', label: 'Loan Term (months)', type: 'NUMBER' },
            { name: 'status', label: 'Status', type: 'SELECT' },
          ],
        },
        {
          name: 'term_sheet_template',
          label: 'Term Sheet Template',
          fields: [
            { name: 'name', label: 'Template Name', type: 'TEXT' },
            { name: 'loan_type', label: 'Loan Type', type: 'SELECT' },
            { name: 'branding', label: 'Branding Config', type: 'TEXT' },
            { name: 'default_terms', label: 'Default Terms', type: 'TEXT' },
          ],
        },
      ],
    },
    version: '1.0.0',
    isDefault: true,
  },

  LOS_TEMPLATE: {
    id: ENTITY_TEMPLATE_IDS.LOS_TEMPLATE,
    name: 'los',
    label: 'Loan Origination System',
    description: 'Loan origination entities: Loans, Properties, Borrowers, Documents',
    category: 'finance',
    icon: 'file-text',
    definition: {
      entities: [
        {
          name: 'loan_application',
          label: 'Loan Application',
          fields: [
            { name: 'loan_number', label: 'Loan Number', type: 'AUTO_NUMBER' },
            { name: 'loan_amount', label: 'Loan Amount', type: 'CURRENCY' },
            { name: 'loan_type', label: 'Loan Type', type: 'SELECT' },
            { name: 'status', label: 'Status', type: 'SELECT' },
          ],
        },
        {
          name: 'property',
          label: 'Property',
          fields: [
            { name: 'address', label: 'Address', type: 'ADDRESS' },
            { name: 'property_type', label: 'Property Type', type: 'SELECT' },
            { name: 'value', label: 'Value', type: 'CURRENCY' },
          ],
        },
      ],
    },
    version: '1.0.0',
    isDefault: false,
  },

  SUPPORT_TEMPLATE: {
    id: ENTITY_TEMPLATE_IDS.SUPPORT_TEMPLATE,
    name: 'support',
    label: 'Support System',
    description: 'Customer support entities: Tickets, Knowledge Base, FAQs',
    category: 'support',
    icon: 'message-square',
    definition: {
      entities: [
        {
          name: 'ticket',
          label: 'Support Ticket',
          fields: [
            { name: 'subject', label: 'Subject', type: 'TEXT' },
            { name: 'description', label: 'Description', type: 'TEXTAREA' },
            { name: 'priority', label: 'Priority', type: 'SELECT' },
            { name: 'status', label: 'Status', type: 'SELECT' },
          ],
        },
        {
          name: 'knowledge_article',
          label: 'Knowledge Article',
          fields: [
            { name: 'title', label: 'Title', type: 'TEXT' },
            { name: 'content', label: 'Content', type: 'RICH_TEXT' },
            { name: 'category', label: 'Category', type: 'SELECT' },
          ],
        },
      ],
    },
    version: '1.0.0',
    isDefault: false,
  },

  REAL_ESTATE_TEMPLATE: {
    id: ENTITY_TEMPLATE_IDS.REAL_ESTATE_TEMPLATE,
    name: 'real_estate',
    label: 'Real Estate Investment',
    description: 'Real estate investment entities: Properties, Units, Tenants, Leases',
    category: 'real_estate',
    icon: 'building',
    definition: {
      entities: [
        {
          name: 'property',
          label: 'Property',
          fields: [
            { name: 'address', label: 'Address', type: 'ADDRESS' },
            { name: 'property_type', label: 'Type', type: 'SELECT' },
            { name: 'units', label: 'Units', type: 'NUMBER' },
            { name: 'noi', label: 'NOI', type: 'CURRENCY' },
          ],
        },
      ],
    },
    version: '1.0.0',
    isDefault: false,
  },
};

// =============================================================================
// ENTITY RECORD DATA (Sample records)
// =============================================================================

export const ENTITY_RECORDS: Record<string, EntityRecordSeedData> = {
  LOAN_MARCUS_THOMPSON: {
    id: ENTITY_RECORD_IDS.LOAN_MARCUS_THOMPSON,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    data: {
      loan_number: 'LOAN-2026-0001',
      loan_amount: 2500000,
      loan_type: 'bridge',
      status: 'in_review',
      ltv_ratio: 0.65,
      purpose: 'purchase',
      term_months: 12,
      interest_rate: 0.1025,
      borrower_name: 'Marcus Thompson',
    },
    searchText: 'LOAN-2026-0001 Marcus Thompson bridge purchase',
    ownerId: USER_IDS.MIKE_CHEN,
    createdBy: USER_IDS.MIKE_CHEN,
  },

  LOAN_JENNIFER_WALSH: {
    id: ENTITY_RECORD_IDS.LOAN_JENNIFER_WALSH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    data: {
      loan_number: 'LOAN-2026-0002',
      loan_amount: 1800000,
      loan_type: 'fix_flip',
      status: 'submitted',
      ltv_ratio: 0.7,
      purpose: 'purchase',
      term_months: 9,
      borrower_name: 'Jennifer Walsh',
    },
    searchText: 'LOAN-2026-0002 Jennifer Walsh fix flip',
    ownerId: USER_IDS.SARAH_WILLIAMS,
    createdBy: USER_IDS.SARAH_WILLIAMS,
  },

  PROPERTY_AUSTIN_OFFICE: {
    id: ENTITY_RECORD_IDS.PROPERTY_AUSTIN_OFFICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.PROPERTY,
    data: {
      address: {
        street: '500 Congress Avenue',
        city: 'Austin',
        state: 'TX',
        zip: '78701',
      },
      property_type: 'commercial',
      value: 5500000,
      sqft: 12000,
      year_built: 2015,
    },
    searchText: '500 Congress Avenue Austin TX commercial',
    createdBy: USER_IDS.MIKE_CHEN,
  },

  CONTACT_MARCUS_THOMPSON: {
    id: ENTITY_RECORD_IDS.CONTACT_MARCUS_THOMPSON,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    data: {
      first_name: 'Marcus',
      last_name: 'Thompson',
      full_name: 'Marcus Thompson',
      email: 'marcus.thompson@investorpro.com',
      phone: '(512) 555-0100',
      company: 'InvestorPro Holdings',
    },
    searchText: 'Marcus Thompson marcus.thompson@investorpro.com',
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

// =============================================================================
// RELATIONSHIP DEFINITION DATA
// =============================================================================

export const RELATIONSHIP_DEFINITIONS: Record<string, RelationshipDefinitionSeedData> = {
  LOAN_TO_PROPERTY: {
    id: RELATIONSHIP_DEFINITION_IDS.LOAN_TO_PROPERTY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'loan_property',
    fromEntityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    fromFieldName: 'properties',
    fromLabel: "Loan's Properties",
    toEntityId: ENTITY_DEFINITION_IDS.PROPERTY,
    toFieldName: 'loans',
    toLabel: "Property's Loans",
    relationshipType: 'MANY_TO_MANY',
    cascadeDelete: false,
    required: false,
  },

  LOAN_TO_BORROWER: {
    id: RELATIONSHIP_DEFINITION_IDS.LOAN_TO_BORROWER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'loan_borrower',
    fromEntityId: ENTITY_DEFINITION_IDS.LOAN_APPLICATION,
    fromFieldName: 'borrowers',
    fromLabel: "Loan's Borrowers",
    toEntityId: ENTITY_DEFINITION_IDS.CONTACT,
    toFieldName: 'loans',
    toLabel: "Contact's Loans",
    relationshipType: 'MANY_TO_MANY',
    cascadeDelete: false,
    required: true,
  },

  CONTACT_TO_COMPANY: {
    id: RELATIONSHIP_DEFINITION_IDS.CONTACT_TO_COMPANY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'contact_company',
    fromEntityId: ENTITY_DEFINITION_IDS.CONTACT,
    fromFieldName: 'company',
    fromLabel: "Contact's Company",
    toEntityId: ENTITY_DEFINITION_IDS.COMPANY,
    toFieldName: 'contacts',
    toLabel: "Company's Contacts",
    relationshipType: 'ONE_TO_MANY',
    cascadeDelete: false,
    required: false,
  },

  DEAL_TO_CONTACT: {
    id: RELATIONSHIP_DEFINITION_IDS.DEAL_TO_CONTACT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'deal_contact',
    fromEntityId: ENTITY_DEFINITION_IDS.DEAL,
    fromFieldName: 'contacts',
    fromLabel: "Deal's Contacts",
    toEntityId: ENTITY_DEFINITION_IDS.CONTACT,
    toFieldName: 'deals',
    toLabel: "Contact's Deals",
    relationshipType: 'MANY_TO_MANY',
    cascadeDelete: false,
    required: false,
  },
};

// =============================================================================
// MATCHING RULE DATA
// =============================================================================

export const MATCHING_RULES: Record<keyof typeof MATCHING_RULE_IDS, MatchingRuleSeedData> = {
  CONTACT_EMAIL_MATCH: {
    id: MATCHING_RULE_IDS.CONTACT_EMAIL_MATCH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'contact_email_match',
    description: 'Match contacts by email address (exact match)',
    priority: 1,
    isActive: true,
    criteria: [{ fieldName: 'email', criteriaType: 'EXACT', weight: 1.0 }],
    exactThreshold: 1.0,
    probableThreshold: 0.85,
    possibleThreshold: 0.7,
    defaultAction: 'AUTO_LINK',
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CONTACT_PHONE_MATCH: {
    id: MATCHING_RULE_IDS.CONTACT_PHONE_MATCH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'contact_phone_match',
    description: 'Match contacts by phone number (normalized)',
    priority: 2,
    isActive: true,
    criteria: [
      { fieldName: 'phone', criteriaType: 'NORMALIZED', weight: 0.8 },
      { fieldName: 'last_name', criteriaType: 'FUZZY_TEXT', weight: 0.2 },
    ],
    exactThreshold: 1.0,
    probableThreshold: 0.85,
    possibleThreshold: 0.7,
    defaultAction: 'FLAG_REVIEW',
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  BORROWER_SSN_MATCH: {
    id: MATCHING_RULE_IDS.BORROWER_SSN_MATCH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.CONTACT,
    name: 'borrower_ssn_match',
    description: 'Match borrowers by SSN hash (privacy-preserving)',
    priority: 0, // Highest priority
    isActive: true,
    criteria: [{ fieldName: 'ssn_hash', criteriaType: 'HASH', weight: 1.0 }],
    exactThreshold: 1.0,
    probableThreshold: 1.0, // SSN must be exact
    possibleThreshold: 1.0,
    defaultAction: 'BLOCK', // Block duplicate SSN entries
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PROPERTY_ADDRESS_MATCH: {
    id: MATCHING_RULE_IDS.PROPERTY_ADDRESS_MATCH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityId: ENTITY_DEFINITION_IDS.PROPERTY,
    name: 'property_address_match',
    description: 'Match properties by address (fuzzy match with normalization)',
    priority: 1,
    isActive: true,
    criteria: [
      { fieldName: 'address.street', criteriaType: 'FUZZY_TEXT', weight: 0.5, options: { threshold: 0.85 } },
      { fieldName: 'address.city', criteriaType: 'EXACT', weight: 0.25 },
      { fieldName: 'address.zip', criteriaType: 'EXACT', weight: 0.25 },
    ],
    exactThreshold: 1.0,
    probableThreshold: 0.9,
    possibleThreshold: 0.8,
    defaultAction: 'SUGGEST',
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllEntityDefinitions(workspace?: string): EntityDefinitionSeedData[] {
  const entities = Object.values(ENTITY_DEFINITIONS);
  if (!workspace) return entities;
  return entities.map((e) => ({ ...e, label: `${e.label} [${workspace}]` }));
}

export function getAllFieldDefinitions(): FieldDefinitionSeedData[] {
  return Object.values(FIELD_DEFINITIONS);
}

export function getAllEntityTemplates(): EntityTemplateSeedData[] {
  return Object.values(ENTITY_TEMPLATES);
}

export function getAllEntityRecords(workspace?: string): EntityRecordSeedData[] {
  const records = Object.values(ENTITY_RECORDS);
  if (!workspace) return records;
  return records.map((r) => ({
    ...r,
    searchText: r.searchText ? `${r.searchText} [${workspace}]` : undefined,
  }));
}

export function getAllRelationshipDefinitions(): RelationshipDefinitionSeedData[] {
  return Object.values(RELATIONSHIP_DEFINITIONS);
}

export function getAllMatchingRules(): MatchingRuleSeedData[] {
  return Object.values(MATCHING_RULES);
}

export function getFieldsByEntity(entityId: string): FieldDefinitionSeedData[] {
  return Object.values(FIELD_DEFINITIONS).filter((f) => f.entityId === entityId);
}

export function getRecordsByEntity(entityId: string): EntityRecordSeedData[] {
  return Object.values(ENTITY_RECORDS).filter((r) => r.entityId === entityId);
}

export function getMatchingRulesByEntity(entityId: string): MatchingRuleSeedData[] {
  return Object.values(MATCHING_RULES).filter((r) => r.entityId === entityId);
}
